import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { tooltipFormatterList } from 'helpers/echart-utils';
import { useTranslation } from 'react-i18next';

echarts.use([TooltipComponent, BarChart]);

interface DailyQty {
  date: string;
  qty: number;
  amount: number;
}

interface EcomProductQtySoldChartProps {
  data: DailyQty[];
  loading?: boolean;
  showAmount?: boolean;
}

const EcomProductQtySoldChart: React.FC<EcomProductQtySoldChartProps> = ({ 
  data = [],
  loading = false,
  showAmount = false
}) => {
  const { getThemeColor } = useAppContext();
  const { t } = useTranslation();

  const getChartOptions = () => {
    // Convert string dates to Date objects for the chart
    const dates = data.map(item => new Date(item.date));
    const values = data.map(item => showAmount ? item.amount : item.qty);
    
    // Calculate the ideal interval for x-axis labels
    const interval = Math.max(1, Math.floor(dates.length / 6));
    
    return {
      tooltip: {
        trigger: 'axis',
        padding: 10,
        backgroundColor: getThemeColor('body-highlight-bg'),
        borderColor: getThemeColor('border-color'),
        textStyle: { color: getThemeColor('light-text-emphasis') },
        borderWidth: 1,
        transitionDuration: 0,
        axisPointer: {
          type: 'none'
        },
        formatter: (params: any) => {
          // Get the complete data item for this date
          const dateObj = params[0].axisValue;
          const dataIndex = params[0].dataIndex;
          const fullItem = data[dataIndex];
          
          if (!fullItem) {
            return '';
          }
          
          const date = dayjs(fullItem.date).format('MMM D, YYYY');
          const qty = fullItem.qty;
          const amount = fullItem.amount;
          
          return `<div style="margin-bottom: 5px"><strong>${date}</strong></div>
                  <div>${t('chart.itemsSold')}: <strong>${qty}</strong></div>
                  <div>${t('chart.revenue')}: <strong>${amount.toFixed(1)} ${t('transactionSummary.dinarSymbol')}</strong></div>`;
        }
      },
      xAxis: [
        {
          type: 'category',
          data: dates,
          show: true,
          boundaryGap: false,
          axisLine: {
            show: true,
            lineStyle: { color: getThemeColor('secondary-bg') }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            formatter: (value: Date) => dayjs(value).format('DD MMM'),
            showMinLabel: true,
            showMaxLabel: false,
            color: getThemeColor('secondary-color'),
            align: 'left',
            interval: interval,
            fontFamily: 'Nunito Sans',
            fontWeight: 600,
            fontSize: 12.8
          }
        },
        {
          type: 'category',
          position: 'bottom',
          show: true,
          data: dates,
          axisLabel: {
            formatter: (value: Date) => dayjs(value).format('DD MMM'),
            interval: dates.length - 1, // Only show the last label
            showMaxLabel: true,
            showMinLabel: false,
            color: getThemeColor('secondary-color'),
            align: 'right',
            fontFamily: 'Nunito Sans',
            fontWeight: 600,
            fontSize: 12.8
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false
          },
          boundaryGap: false
        }
      ],
      yAxis: {
        show: false,
        type: 'value',
        boundaryGap: false
      },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: '60%',
          itemStyle: {
            color: getThemeColor('primary'),
            borderRadius: [3, 3, 0, 0] // Rounded top corners
          }
        }
      ],
      grid: { left: 0, right: 0, top: 5, bottom: 20 }
    };
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getChartOptions()}
      style={{ height: '180px', width: '100%' }}
      notMerge={true}
    />
  );
};

export default EcomProductQtySoldChart;
